import React from "react";
import TeamList from "./components/TeamList";
import TeamDetails from "./components/TeamDetails";
import UserSettings from "./components/UserSettings";
import DefaultTargets from "./components/DefaultTargets";
import SecuritySettings from "./components/SecuritySettings";
import MortgageSchedules from "./components/MortgageSchedules";
import styled from "@emotion/styled";

const Settings = ({ teamId, userFullName, userId }) => {

  return (
    <SettingsPage>
      <SettingsWorkspace>
        <SettingsIntro>
          <div>
            <SettingsEyebrow>Administration</SettingsEyebrow>
            <SettingsTitle>Settings</SettingsTitle>
          </div>
          <SettingsSummary>
            Team configuration, account defaults, security controls, and mortgage assumptions.
          </SettingsSummary>
        </SettingsIntro>
        <SettingsScroll className="verticalOnly">
          <SettingsStack>
            <TeamDetails />
            <SecuritySettings userFullName={userFullName} teamId={teamId} />
            <MortgageSchedules />
            <DefaultTargets teamId={teamId} />
            <UserSettings userFullName={userFullName} userId={userId} />
            <TeamList userId={userId} />
          </SettingsStack>
        </SettingsScroll>
      </SettingsWorkspace>
    </SettingsPage>
  )    
}

const SettingsPage = styled.main(({ theme }) => `
  width: 100%;
  height: 100%;
  min-height: 0;
  display: grid;
  padding: 1rem;
  overflow: hidden;
  background: ${theme.palette.common.pageBackground};
`)

const SettingsWorkspace = styled.section(({ theme }) => `
  width: min(100%, 72rem);
  height: 100%;
  max-height: 100%;
  justify-self: center;
  align-self: stretch;
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  overflow: hidden;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.9rem;
  background: ${theme.palette.common.cardBackground};
  box-shadow: inset 0 0.1rem 0 rgba(255, 255, 255, 0.72);
`)

const SettingsIntro = styled.header(({ theme }) => `
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0.45rem;
  align-items: start;
  padding: 1.35rem 1.5rem 1.15rem;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.white};
`)

const SettingsEyebrow = styled.div(({ theme }) => `
  margin-bottom: 0.25rem;
  color: ${theme.palette.primary.main};
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.04rem;
  text-transform: uppercase;
`)

const SettingsTitle = styled.h1(({ theme }) => `
  margin: 0;
  color: ${theme.palette.common.black};
  font-size: 1.7rem;
  font-weight: 800;
  line-height: 1.15;
`)

const SettingsSummary = styled.p(({ theme }) => `
  margin: 0;
  color: ${theme.palette.common.grayText};
  font-size: 0.96rem;
  font-weight: 550;
  line-height: 1.45;
`)

const SettingsScroll = styled.div(({ theme }) => `
  min-height: 0;
  height: 100%;
  max-height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 1.15rem;

  ::-webkit-scrollbar-thumb {
    border: 0.1rem solid ${theme.palette.common.grayText};
    background-color: ${theme.palette.common.grayText};
  }

  ::-webkit-scrollbar {
    -webkit-appearance: none;
    border: 0.05rem solid ${theme.palette.common.grayBorderDark};
    background-color: ${theme.palette.common.pageBackground};
  }

  ::-webkit-scrollbar:vertical {
    width: 0.4rem;
  }
`)

const SettingsStack = styled.div(({ theme }) => `
  width: 100%;
  min-width: 0;
  display: grid;
  gap: 1rem;
  margin: 0 auto;

  > div {
    width: 100% !important;
    min-width: 0;
    max-width: 100%;
    margin: 0 !important;
    padding: 0 !important;
    align-items: stretch;
    border: 0.1rem solid ${theme.palette.common.grayBorder};
    border-radius: 0.8rem;
    background: ${theme.palette.common.white};
    box-shadow: 0 0.25rem 0.9rem rgba(15, 23, 42, 0.05);
    overflow: hidden;
  }

  > div > div:first-of-type {
    width: 100%;
    min-height: 3.2rem;
    margin: 0 !important;
    padding: 0.85rem 1rem !important;
    justify-content: flex-start;
    border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
    background: ${theme.palette.common.grayBorderLight};
    color: ${theme.palette.common.black};
    font-size: 1rem;
    font-weight: 800;
    line-height: 1.2;
  }

  > div > div:not(:first-of-type) {
    max-width: 100%;
  }

  img {
    width: min(100%, 32rem) !important;
    max-width: 100%;
    height: auto !important;
    margin: 1.25rem auto 0 !important;
    display: block;
  }

  .green,
  .red {
    color: ${theme.palette.common.white} !important;
  }

  .green {
    background: ${theme.accent.success.primary} !important;
  }

  .red {
    background: ${theme.palette.common.redMediumTransparent} !important;
    color: ${theme.palette.common.redDark} !important;
  }

  .subHeader {
    width: 100% !important;
    margin: 0 !important;
    background: ${theme.palette.common.cardBackground} !important;
  }

  .detail {
    line-height: 1.35;
  }

  .listMembers,
  .mortgageSchedules {
    width: 100%;
  }

  button {
    border-radius: 0.7rem;
    font-size: 1rem;
    font-weight: 700;
  }
`)

export default Settings;
